import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormFields,
    FormActions,
    FormDropdown,
    mapToDropdownOption,
    ValidationResult,
    FormValidationSummary,
    useNotifications
} from "sprint-common-web";

import { AspectRatio } from "../../../../../../../../common/data/lookup-data-providers/AspectRatiosDataProvider";
import { AspectType } from "../../../../../../../../common/data/lookup-data-providers/AspectTypesDataProvider";
import { useAspectRatios } from "../../../../../../../../common/data/lookup-data-providers/useAspectRatios";
import { useAspectTypes } from "../../../../../../../../common/data/lookup-data-providers/useAspectTypes";
import { useCloudAssetDetails } from "../../../../../common/hooks/useCloudAssetDetails";
import { useEditCloudVideoAssetTechnical } from "./useEditCloudVideoAssetTechnical";

export interface VideoTechnicalFormProps {
    onDismiss(): void;
    onSubmit(newAssetId: string): void;
    assetId: string;
    aspectRatioId?: string;
    aspectTypeId?: string;
}

interface VideoTechnicalFormData {
    aspectRatioId: string;
    aspectTypeId: string;
}

export const CloudVideoAssetTechnicalForm = (props: VideoTechnicalFormProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [originalAspectRatio, setOriginalAspectRatio] = useState<AspectRatio | undefined>(undefined);
    const [validationResult, setValidationResult] = useState<ValidationResult>();
    const { success, error } = useNotifications();
    const { data: assetDetails } = useCloudAssetDetails();
    const { validateCloudVideoAssetTechnical, updateCloudVideoAssetTechnical } = useEditCloudVideoAssetTechnical({
        invalidationKey: assetDetails?.assetId
    });
    const { data: aspectRatios } = useAspectRatios();
    const { data: aspectTypes } = useAspectTypes();

    const { handleSubmit, errors, control, formState, reset, getValues } = useForm<VideoTechnicalFormData>();

    useEffect(() => {
        let defaultAspectRatio = undefined;
        let defaultAspectType = undefined;

        if (aspectRatios) {
            defaultAspectRatio = aspectRatios?.find(
                (aspectRatio: AspectRatio) => aspectRatio.id === props.aspectRatioId
            );
            setOriginalAspectRatio(defaultAspectRatio);
        }

        if (aspectTypes) {
            defaultAspectType = aspectTypes?.find((aspectType: AspectType) => aspectType.id === props.aspectTypeId);
        }

        reset({
            ...getValues(),
            aspectRatioId: defaultAspectRatio?.id,
            aspectTypeId: defaultAspectType?.id
        });
    }, [aspectRatios, aspectTypes]);

    const hasAspectRatioChanged = (aspectRatioId: string): boolean => {
        return originalAspectRatio?.id !== aspectRatioId;
    };

    const handleUpdate = async ({ aspectRatioId, aspectTypeId }: VideoTechnicalFormData) => {
        setIsLoading(true);

        try {
            if (hasAspectRatioChanged(aspectRatioId)) {
                const result = await validateAndSetResult(aspectRatioId);

                if (!result.isValid) {
                    setIsLoading(false);
                    return;
                }
            }

            const updateResponse = await updateCloudVideoAssetTechnical.mutateAsync({
                assetId: props.assetId,
                aspectRatioId,
                aspectTypeId
            });

            props.onSubmit(updateResponse.newAssetId);
            success("Video asset technical details updated successfully.");
        } catch (e) {
            error("Failed to update video asset technical details.");
        } finally {
            setIsLoading(false);
        }
    };

    const validateAndSetResult = async (aspectRatioId: string) => {
        const result = await validateCloudVideoAssetTechnical.mutateAsync({
            assetId: props.assetId,
            aspectRatioId
        });
        setValidationResult(result);

        return result;
    };

    return (
        <Form onSubmit={handleSubmit(handleUpdate)}>
            <FormFields>
                <FormDropdown
                    options={aspectRatios?.map((ratio) => mapToDropdownOption(ratio)) ?? []}
                    control={control}
                    disableClear={true}
                    label="Aspect Ratio"
                    name="aspectRatioId"
                    errors={errors}
                    disabled={!aspectRatios?.length}
                    rules={{ required: "Aspect Ratio is required." }}
                />

                <FormDropdown
                    options={aspectTypes?.map((type) => mapToDropdownOption(type)) ?? []}
                    control={control}
                    label="Aspect Type"
                    name="aspectTypeId"
                    errors={errors}
                    disabled={!aspectTypes?.length}
                />

                {validationResult && <FormValidationSummary validationResult={validationResult} />}
                <FormActions
                    onCancel={props.onDismiss}
                    isLoading={formState.isValidating || formState.isSubmitting || isLoading}
                    submitDisabled={!formState.isDirty}
                />
            </FormFields>
        </Form>
    );
};
